import FocusableNode from '../Tree/FocusableNode';

import {TFocusableEventType} from './Constants/FocusableEventType';

/**
 * A DOM-event like structure propagated from focusable nodes as events are dispatched on them.
 *
 * When a "focusable event" is handled by a focusable component, an instance of `FocusableEvent`
 * will be sent as an argument to the handler function.
 *
 * Just as with DOM events, a `target` property is exposed pointing to the `FocusableNode` from
 * which the event was dispatched.
 *
 * `.stopPropagation()` and `.preventDefault()` methods are implemented, mirroring their respective
 * DOM event behaviours.
 *
 * In some cases, the `detail` property of the event may be populated with event-specific data,
 * similar to a DOM `CustomEvent`.
 */

interface IFocusableEventOptions<TDetail> {
    canPropagate?: boolean;
    defaultPrevented?: boolean;
    detail?: TDetail;
}

class FocusableEvent<TDetail = any> {
    public type: TFocusableEventType;
    public target!: FocusableNode;
    public detail: TDetail = {} as any;
    public canPropagate!: boolean;
    public defaultPrevented!: boolean;
    public stopPropagation: () => void;
    public preventDefault: () => void;

    constructor(type: TFocusableEventType, options: IFocusableEventOptions<TDetail> = {}) {
        this.type = type;

        if (typeof options.detail !== 'undefined') {
            this.detail = options.detail;
        }

        Object.defineProperties(this, {
            canPropagate: {
                get: () => options.canPropagate ?? true,
            },
            defaultPrevented: {
                get: () => options.defaultPrevented ?? false,
            },
        });

        this.stopPropagation = () => (options.canPropagate = false);
        this.preventDefault = () => (options.defaultPrevented = true);
    }
}

export default FocusableEvent;
